@use '../../../scss/utility';
@use './accordion-item-vars';

:host {
  @include accordion-item-vars.wb-accordion-item-vars;

  --content-height: auto;
  --section-max-height: 0;
  --section-overflow: hidden;
  --transition-time: 300ms;

  display: block;
  background-color: var(--accordion-item-background-color);
  border-block-end: var(--accordion-item-border-weight) solid var(--accordion-item-border-color);

  transition: background-color var(--transition-time) var(--wb-fade);

  @include utility.breakpoint-from(mq4) {
    @include accordion-item-vars.wb-accordion-item-desktop-vars;
  }
}

:host(:first-child) {
  border-block-start: var(--accordion-item-border-weight) solid var(--accordion-item-border-color);
}

:host(:hover) {
  @include accordion-item-vars.wb-accordion-item-hover-vars;
}

:host(:focus-within) {
  @include accordion-item-vars.wb-accordion-item-focus-vars;
  outline: var(--accordion-item-border-weight) solid var(--accordion-item-border-ring-color);
}

.section {
  display: block;
  max-height: var(--section-max-height);
  overflow: var(--section-overflow);
  transition: all var(--transition-time) var(--wb-open);

  @media (prefers-reduced-motion) {
    transition: max-height 0s;
  }
}

.has-overflow {
  --section-overflow: visible;
}

.toggle {
  background-color: transparent;
  color: var(--accordion-item-text-headline-color);
  appearance: none;
  border: none;
  display: flex;
  justify-content: space-between;
  gap: var(--accordion-item-header-gap);
  align-items: center;
  margin: 0;
  text-align: start;
  padding-block: var(--accordion-item-padding-block);
  padding-inline: var(--accordion-item-padding-inline);

  &:hover:not(:disabled) {
    cursor: pointer;
  }
}

.header {
  flex: 1;
}

.inner {
  display: flex;
}

.header-icon {
  display: flex;

  ::slotted([slot='icon']) {
    margin-inline-end: var(--accordion-item-headline-gap);

    // TODO: Standardize with variables
    @include utility.breakpoint-from('mq4') {
      // fix alignment between icon and headline, they need to be align-center
      // from mq1-3 headline is 24px, same as icons
      // from mq4-6 headline is 28px and icons remains 24px
      margin-block-start: calc((var(--wb-typography-font-line-height-unit-7x) - var(--size)) / 2);
    }
  }
}

.headline {
  display: flex;
  gap: var(--accordion-item-headline-gap);
}

::slotted([slot='icon']) {
  color: var(--accordion-item-icon-color);
  flex-shrink: 0;
}

.round-button {
  flex-shrink: 0;
  pointer-events: none;
}

.icon {
  transform: rotate(90deg);
  flex-shrink: 0;
}

.mobile-header {
  display: none;

  ::part(button) {
    justify-content: start;
    padding-block: var(--accordion-item-padding-block);
    padding-inline: var(--accordion-item-padding-inline);
  }
}

.close {
  width: 100vw;
  border-block-end: var(--accordion-item-border-weight) solid;
  height: utility.wb-pixel-to-rem(58px);
}

.content {
  box-sizing: border-box;
  display: block;
  padding-block: var(--accordion-item-padding-block);
  padding-inline: var(--accordion-item-padding-inline);
  padding-block-start: 0;
  height: var(--content-height);
  color: var(--accordion-item-text-body-color);
}

::slotted(.wb-counter:not([slot='counter'])) {
  position: relative;
  inset-block-start: utility.wb-pixel-to-rem(-2px);
}

:host([prevent-mobile-flyout]:dir(rtl)) .icon {
  transform: rotate(270deg);
}

:host(:not([prevent-mobile-flyout])) {
  @include utility.breakpoint-to('mq3') {
    .section {
      width: 100%;
      min-height: 100dvh;
      position: fixed;
      inset-block: 0;
      inset-inline-start: 100vw;
      transform: translate3d(0, 0, 0);
      transition: transform 0.65s var(--wb-move);
      background-color: var(--accordion-item-fly-in-background);
      z-index: utility.$wb-z-index-accordion-item;

      @media (prefers-reduced-motion) {
        transition: transform 0s;
      }
    }

    .mobile-header {
      display: block;
    }

    .close {
      border-color: var(--accordion-item-border-color);
    }

    .icon {
      transform: none;
    }

    .content {
      padding-block: var(--accordion-item-padding-block);
      padding-inline: var(--accordion-item-padding-inline);
    }
  }

  :dir(rtl) {
    @include utility.breakpoint-from('mq4') {
      .icon {
        transform: rotate(-90deg);
      }
    }
  }
}

:host([open]) {
  @include accordion-item-vars.wb-accordion-item-open-vars;

  .icon {
    transform: rotate(270deg);
  }

  .section {
    height: auto;
  }
}

:host([open]:hover) {
  @include accordion-item-vars.wb-accordion-item-open-hover-vars;
}

:host([open]:focus-within) {
  @include accordion-item-vars.wb-accordion-item-open-focus-vars;
}

:host([open]:not([prevent-mobile-flyout])) {
  @include utility.breakpoint-to('mq3') {
    .section {
      transform: translate3d(-100vw, 0, 0);
    }

    .content {
      overflow-y: auto;
    }
  }

  :dir(rtl) {
    @include utility.breakpoint-to('mq3') {
      &.section {
        transform: translate3d(100vw, 0, 0);
      }
    }
  }
}

:host(:dir(rtl)[open][prevent-mobile-flyout]) .icon {
  @include utility.breakpoint-to('mq3') {
    transform: rotate(90deg);
  }
}

:host([subtle]),
:host([subtle][open]) {
  @include accordion-item-vars.wb-accordion-item-subtle-vars;
}

:host([subtle]:hover),
:host([subtle][open]:hover) {
  @include accordion-item-vars.wb-accordion-item-subtle-hover-vars;
}

:host([subtle]:focus-within) {
  @include accordion-item-vars.wb-accordion-item-subtle-focus-vars;
}

:host([subtle][open]:focus-within) {
  @include accordion-item-vars.wb-accordion-item-subtle-open-focus-vars;
}
